import axios from 'axios';
import { baseURL, generalEndpoint } from '../config';

const API_BASE_URL = process.env.REACT_APP_API_URL || baseURL + '/' + generalEndpoint;

/**
 * Add a new Liquidity Provider
 * @param {string} token - Authentication token
 * @param {Object} lpData - LP data object
 * @param {string} lpData.name - LP name (required)
 * @param {boolean} lpData.isActive - Whether LP is active
 * @param {number} lpData.priority - Priority (1-100)
 * @param {string} lpData.lpType - LP type
 * @param {string} lpData.region - Region
 * @param {boolean} lpData.isEcn - Whether it's an ECN
 * @param {number} lpData.creditLimit - Credit limit
 * @param {string} lpData.notes - Additional notes
 * @returns {Promise<Object>} The API response
 */
export const addLiquidityProvider = async (token, lpData) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        console.log('API Request - URL:', `${API_BASE_URL}/Lp`);
        console.log('API Request - Data:', lpData);
        console.log('API Request - Config:', config);
        
        const response = await axios.post(
            `${API_BASE_URL}/Lp`,
            lpData,
            config
        );
        
        console.log('API Response:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error adding liquidity provider:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Get all liquidity providers
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} The API response
 */
export const getLiquidityProviders = async (token) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        const response = await axios.get(
            `${API_BASE_URL}/Lp`,
            config
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching liquidity providers:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Get a specific liquidity provider by ID
 * @param {string} token - Authentication token
 * @param {string|number} lpId - LP ID
 * @returns {Promise<Object>} The LP data
 */
export const getLiquidityProviderById = async (token, lpId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    };

    try {
        console.log(`📡 API: Fetching LP with ID ${lpId}`);
        const response = await axios.get(
            `${API_BASE_URL}/Lp/${lpId}`,
            config
        );
        console.log(`✅ API: LP ${lpId} fetched:`, response.data);
        return response.data;
    } catch (error) {
        console.error(`❌ Error fetching LP ${lpId}:`, error);
        throw error.response?.data || error.message;
    }
};

/**
 * Update an existing Liquidity Provider
 * @param {string} token - Authentication token
 * @param {number} lpId - LP ID to update
 * @param {Object} lpData - LP data object
 * @param {string} lpData.name - LP name (required)
 * @param {boolean} lpData.isActive - Whether LP is active
 * @param {number} lpData.priority - Priority (1-100)
 * @param {string} lpData.lpType - LP type
 * @param {string} lpData.region - Region
 * @param {boolean} lpData.isEcn - Whether it's an ECN
 * @param {number} lpData.creditLimit - Credit limit
 * @param {string} lpData.notes - Additional notes
 * @returns {Promise<Object>} The API response
 */
export const updateLiquidityProvider = async (token, lpId, lpData) => {
    const config = {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        const response = await axios.put(
            `${API_BASE_URL}/Lp/${lpId}`,
            lpData,
            config
        );
        return response.data;
    } catch (error) {
        console.error('Error updating liquidity provider:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Get available liquidity providers
 * @param {string} token - Authentication token
 * @returns {Promise<Object>} The API response
 */
export const getAvailableLPs = async (token) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
    };

    const startTime = Date.now();

    try {
        const response = await axios.get(
            `${API_BASE_URL}/Lp/available`,
            config
        );
        
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        if (duration > 5000) {
            console.warn(`⚠️ Slow API response: ${duration}ms for /Lp/available`);
        }
        
        return response.data;
    } catch (error) {
        const endTime = Date.now();
        const duration = endTime - startTime;
        
        if (error.code === 'ECONNABORTED') {
            throw new Error(`Request timeout after ${duration}ms. The server is taking too long to respond.`);
        }
        
        throw error.response?.data || error.message;
    }
};


/**
 * Delete a liquidity provider
 * @param {string} token - Authentication token
 * @param {string} lpId - LP ID
 * @returns {Promise<Object>} The API response
 */
export const deleteLiquidityProvider = async (token, lpId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        const response = await axios.delete(
            `${API_BASE_URL}/Lp/${lpId}`,
            config
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting liquidity provider:', error);
        throw error.response?.data || error.message;
    }
};

/**
 * Get LP status and metrics
 * @param {string} token - Authentication token
 * @param {string} lpId - LP ID
 * @returns {Promise<Object>} The API response
 */
export const getLPStatus = async (token, lpId) => {
    const config = {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    try {
        const response = await axios.get(
            `${API_BASE_URL}/Lp/${lpId}/status`,
            config
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching LP status:', error);
        throw error.response?.data || error.message;
    }
};
